import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { AuditSheetNavigationItem, ReportHanlder } from "models/propTypes/accountingPropTypes";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyEnums } from "models/enums";
import { ReconciliationReportProps } from "models/propTypes/accountingPropTypes";
import { FinancialReportDateFilter } from "models/formTypes/accounting";
import { DateRange } from "models/formTypes/util";
import { NestedTableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { useFinancialReportRow, useTransactionAmount } from "components/accounting";
import { DrillDownTable, ViewPanel } from "components/common";
import useReconciliationReportHelpers from "./hooks/useReconciliationReportHelpers";
import ReconciliationStatus from "./reconciliationStatus";
import ReconciliationSpecificationRowActionButtons from "./specificationRowActionButtons";
import { buildAuditSheetNavigationList, getAccountTitle } from "./widgets/auditSheetNavigation";
import { useFinancialReportService } from "serviceHooks";
//#endregion
import { DateHelper, FinancialReportHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const ReconciliationReport: React.ForwardRefRenderFunction<ReportHanlder, ReconciliationReportProps> = (
  { reloadKey, reload, initialFilterReconciliationId, initialReconciliation, filterValues },
  ref
) => {
  const financialReportService = useFinancialReportService();
  const url = document.URL;
  const [companyBooksFilter, setCompanyBooksFilter] = React.useState<FinancialReportDateFilter | undefined>();
  const tableRef = React.useRef<NestedTableHandles>(null);

  const { result, loading, pageFilter, startDate, endDate, startDateHeader, endDateHeader } =
    useReconciliationReportHelpers(
      initialFilterReconciliationId!,
      initialReconciliation!,
      financialReportService.getAuditBalanceReport
    );

  const reconciliationDateFooter: string = React.useMemo(() => {
    const reconciliationId = pageFilter?.reconciliationId ?? initialFilterReconciliationId;
    const filterItem = filterValues.find((obj) => obj.reconciliationId === reconciliationId);

    return filterItem?.endMonth ? DateHelper.formatAPIYearMonthToNumericString(filterItem?.endMonth!) : "";
  }, [initialFilterReconciliationId, pageFilter?.reconciliationId, filterValues]);

  const { descriptionFormatter } = useFinancialReportRow(true);

  React.useEffect(() => {
    const searchIndex = url.search("#");
    const rowId = url.slice(searchIndex + 1, url.length);

    if (result.length >= 1) {
      const index = result.findIndex((x) => x.id === rowId);
      if (index >= 0 && tableRef.current) {
        tableRef.current.expandIndex(index);
      }
    }
  }, [result, url]);

  React.useEffect(() => {
    if (startDate !== undefined && endDate !== undefined) {
      const reconciliationRange: DateRange = {
        start: new Date(startDate),
        end: new Date(endDate),
      };

      const filter: FinancialReportDateFilter = {
        dimensions: [],
        columns: [
          {
            selection: undefined,
            range: reconciliationRange,
          },
        ],
      };

      setCompanyBooksFilter(filter);
    }
  }, [startDate, endDate]);

  const ibFormatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(CurrencyEnums.CurrencyCode.SEK, row.iB);

  const ubFormatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(CurrencyEnums.CurrencyCode.SEK, row.uB);

  const checkedFormatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) => (
    <ReconciliationStatus isChecked={row.checked ?? false} />
  );

  const navigationItemsByColumn = React.useMemo(() => new Map<number, AuditSheetNavigationItem[]>(), [result]);
  const getNavigationItems = (columnIndex: number) => {
    let items = navigationItemsByColumn.get(columnIndex);
    if (!items) {
      items = buildAuditSheetNavigationList(result, columnIndex);
      navigationItemsByColumn.set(columnIndex, items);
    }
    return items;
  };

  const auditSheetIdFormatter = (
    row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn,
    rowIndex: number,
    depth?: number,
    repeatIndex?: number,
    parentRow?: WintSuperkollAPIModelFinancialReport.FinancialReportRow
  ) => {
    return row.auditSheetId ? (
      <ReconciliationSpecificationRowActionButtons
        auditSheetId={row.auditSheetId}
        auditSheetTitle={parentRow ? getAccountTitle(parentRow) : ""}
        navigationItems={getNavigationItems(repeatIndex ?? 0)}
      />
    ) : undefined;
  };

  React.useEffect(() => {
    reload();
  }, [pageFilter?.reconciliationId]);

  const { transactionAmountFormatter } = useTransactionAmount(
    companyBooksFilter,
    undefined,
    CurrencyEnums.CurrencyCode.SEK,
    true
  );

  return (
    <Grid item container className={cssExports.reconciliationReport}>
      <ViewPanel className={cssExports.reconciliationReportWrapper}>
        <DrillDownTable<
          WintSuperkollAPIModelFinancialReport.FinancialReportRow,
          WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn
        >
          tableRef={tableRef}
          key={reloadKey}
          rows={result}
          rowTextProperty={{
            key: "description",
            properties: {
              description: TranslationKeys.Common.Name,
              formatter: descriptionFormatter,
            },
          }}
          repeatProperties={{
            key: "columns",
            properties: {
              iB: {
                description: startDateHeader,
                formatter: ibFormatter,
                alignment: "right",
                className: cssExports.titleCell,
              },
              difference: {
                description: TranslationKeys.Accounting.Change,
                formatter: transactionAmountFormatter,
                alignment: "right",
                className: cssExports.repeatTitleCell,
              },
              uB: {
                description: endDateHeader,
                formatter: ubFormatter,
                alignment: "right",
                className: cssExports.titleCell,
              },
              checked: {
                description: TranslationKeys.Common.Status,
                formatter: checkedFormatter,
                alignment: "center",
                width: 40,
              },
              auditSheetId: {
                description: "",
                formatter: auditSheetIdFormatter,
                alignment: "right",
                width: 80,
              },
            },
          }}
          childrenKey="children"
          rowDisplayType={FinancialReportHelper.getBalanceReportRowDisplayType}
          loading={loading}
        />

        <Grid className={clsx(cssExports.text, cssExports.footer)}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.ReconciledTo, undefined, {
            date: reconciliationDateFooter,
          })}
        </Grid>
      </ViewPanel>
    </Grid>
  );
};

export default ReconciliationReport;
